import { useState } from 'react';
import { Button, Modal } from '@/shared/components';
import { PermissionGuard } from '@/features/auth';

interface Props {
  settingsCount: number;
  unlinkedCount: number;
  keyName: string;
  onBulkLink: (opts?: { force?: boolean }) => void;
  isBulkLinking: boolean;
}

/**
 * Mirrors `smr-admin/components/WhereUsedPanel.tsx`. Surfaces the gated
 * bulk-link action for the dealer config-options catalog, gated on
 * `dealer.config-setting.bulk-link`.
 *
 * The force-link path is confirmed through the shared `Modal` (CLAUDE.md:
 * "modals use shared Modal") rather than the native `confirm()` prompt. The
 * shared Modal carries the accessibility contract (`role="dialog"`,
 * `aria-modal`, Escape-to-close, a labelled heading) that a native confirm
 * cannot. NOTE: the smr mirror source still uses native `confirm()` — the same
 * latent issue, tracked as a separate smr follow-up (not touched here).
 */
export function DealerConfigOptionWhereUsedPanel({
  settingsCount,
  unlinkedCount,
  keyName,
  onBulkLink,
  isBulkLinking,
}: Props) {
  const [isForceConfirmOpen, setIsForceConfirmOpen] = useState(false);

  const confirmForceLink = () => {
    setIsForceConfirmOpen(false);
    onBulkLink({ force: true });
  };

  return (
    <div className="border-t border-mp-border pt-4 space-y-2">
      <h2 className="text-lg font-semibold">Where used</h2>
      <p className="text-sm">
        {settingsCount === 0
          ? 'This option is not currently used by any dealer setting.'
          : `This option is used by ${settingsCount} dealer setting(s).`}
      </p>

      {unlinkedCount > 0 && (
        <div className="border border-mp-border rounded p-3 space-y-2 bg-mp-background">
          <p className="text-sm">
            <strong>{unlinkedCount}</strong> unlinked setting(s) use the key{' '}
            <code>{keyName}</code> and could be linked to this catalog option.
          </p>
          <PermissionGuard permission={{ resource: 'dealer', action: 'config-setting.bulk-link' }}>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                disabled={isBulkLinking}
                onClick={() => onBulkLink()}
              >
                {isBulkLinking ? 'Linking…' : `Link ${unlinkedCount} unlinked setting(s)`}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isBulkLinking}
                onClick={() => setIsForceConfirmOpen(true)}
              >
                Force link
              </Button>
            </div>

            <Modal
              isOpen={isForceConfirmOpen}
              onClose={() => setIsForceConfirmOpen(false)}
              ariaLabelledBy="dealer-config-force-link-heading"
              className="max-w-md p-6"
            >
              <h2
                id="dealer-config-force-link-heading"
                className="text-lg font-semibold mb-2"
              >
                Force-link without revalidating?
              </h2>
              <p className="text-sm text-mp-muted mb-4">
                Force-linking will link the {unlinkedCount} unlinked setting(s)
                using the key <code>{keyName}</code> without revalidating their
                existing values. This cannot be undone automatically.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="clear"
                  disabled={isBulkLinking}
                  onClick={() => setIsForceConfirmOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={isBulkLinking}
                  onClick={confirmForceLink}
                >
                  {isBulkLinking ? 'Linking…' : 'Force link'}
                </Button>
              </div>
            </Modal>
          </PermissionGuard>
        </div>
      )}
    </div>
  );
}
